import { clear_token, get_token, get_user_details } from "@/router";

const fallback_inactivity_timeout_ms = 10 * 60 * 1000;
const config_refresh_ms = 60 * 1000;
const activity_events = [
  "click",
  "keydown",
  "mousedown",
  "mousemove",
  "scroll",
  "touchstart",
  "wheel"
];

let router_instance = null;
let idle_timer_id = null;
let config_refresh_timer_id = null;
let config_request_id = 0;
let is_monitoring_activity = false;
let is_expiring_session = false;
let last_activity_at = Date.now();
let current_timeout_ms = fallback_inactivity_timeout_ms;
let current_is_unlimited = false;

function get_session_timeout_config_url() {
  if (process.env.VUE_APP_SESSION_TIMEOUT_CONFIG_URL) {
    return process.env.VUE_APP_SESSION_TIMEOUT_CONFIG_URL;
  }

  const source_url = process.env.VUE_APP_LOGOUT_URL
    || process.env.VUE_APP_LOGIN_URL
    || process.env.VUE_APP_VIEW_USER_URL
    || "";

  const api_marker = "/system_management_api/";
  const marker_index = source_url.indexOf(api_marker);

  if (marker_index === -1) {
    return "";
  }

  return `${source_url.slice(0, marker_index)}${api_marker}session_timeout_config/`;
}

function route_requires_auth(route) {
  return Boolean(route?.matched?.some((record) => record.meta?.requiresAuth));
}

function clear_idle_timer() {
  if (idle_timer_id) {
    clearTimeout(idle_timer_id);
    idle_timer_id = null;
  }
}

function clear_config_refresh_timer() {
  if (config_refresh_timer_id) {
    clearInterval(config_refresh_timer_id);
    config_refresh_timer_id = null;
  }
}

function should_monitor_current_route() {
  return Boolean(router_instance && route_requires_auth(router_instance.currentRoute.value) && get_token());
}

function remove_activity_listeners() {
  if (!is_monitoring_activity) {
    return;
  }

  activity_events.forEach((event_name) => {
    window.removeEventListener(event_name, handle_activity, true);
  });
  document.removeEventListener("visibilitychange", handle_visibility_change);
  is_monitoring_activity = false;
}

function add_activity_listeners() {
  if (is_monitoring_activity) {
    return;
  }

  activity_events.forEach((event_name) => {
    window.addEventListener(event_name, handle_activity, {
      capture: true,
      passive: true
    });
  });
  document.addEventListener("visibilitychange", handle_visibility_change);
  is_monitoring_activity = true;
}

function stop_session_timeout_monitoring() {
  clear_idle_timer();
  clear_config_refresh_timer();
  remove_activity_listeners();
  current_is_unlimited = false;
}

function normalize_timeout_ms(value) {
  const timeout_ms = Number(value);

  return Number.isFinite(timeout_ms) && timeout_ms > 0
    ? timeout_ms
    : fallback_inactivity_timeout_ms;
}

function get_elapsed_idle_ms() {
  return Math.max(0, Date.now() - last_activity_at);
}

function schedule_idle_timer() {
  clear_idle_timer();

  if (!should_monitor_current_route() || current_is_unlimited) {
    return;
  }

  const remaining_ms = Math.max(0, current_timeout_ms - get_elapsed_idle_ms());
  idle_timer_id = setTimeout(handle_idle_timeout, remaining_ms);
}

function handle_activity() {
  if (!should_monitor_current_route() || current_is_unlimited) {
    return;
  }

  last_activity_at = Date.now();
  schedule_idle_timer();
}

function handle_visibility_change() {
  if (!should_monitor_current_route()) {
    return;
  }

  if (document.visibilityState === "visible") {
    if (!current_is_unlimited && get_elapsed_idle_ms() >= current_timeout_ms) {
      handle_idle_timeout();
      return;
    }

    refresh_session_timeout_config();
  }
}

async function notify_backend_logout(auth_token) {
  const logout_url = process.env.VUE_APP_LOGOUT_URL;

  if (!logout_url || !auth_token) {
    return;
  }

  const user_details = get_user_details();

  try {
    await fetch(logout_url, {
      method: "POST",
      headers: {
        Authorization: `Token ${auth_token}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        user_id: user_details.user_id
      })
    });
  } catch (error) {
    console.warn("Unable to notify backend about the expired session.", error);
  }
}

async function handle_idle_timeout() {
  if (is_expiring_session || !should_monitor_current_route()) {
    return;
  }

  is_expiring_session = true;
  const auth_token = get_token();

  stop_session_timeout_monitoring();
  await notify_backend_logout(auth_token);
  clear_token();
  sessionStorage.setItem("session_expired_message", "Your session expired because of inactivity.");

  if (router_instance) {
    await router_instance.replace({
      path: "/login",
      query: { session: "expired" }
    });
  }

  is_expiring_session = false;
}

function apply_session_timeout_config(config = {}) {
  current_is_unlimited = Boolean(config.is_session_unlimited || config.session_policy === "unlimited");
  current_timeout_ms = normalize_timeout_ms(config.inactivity_timeout_ms);

  if (current_is_unlimited) {
    remove_activity_listeners();
    clear_idle_timer();
    return;
  }

  add_activity_listeners();

  if (get_elapsed_idle_ms() >= current_timeout_ms) {
    handle_idle_timeout();
    return;
  }

  schedule_idle_timer();
}

async function refresh_session_timeout_config() {
  const auth_token = get_token();
  const config_url = get_session_timeout_config_url();
  const request_id = ++config_request_id;

  if (!should_monitor_current_route()) {
    stop_session_timeout_monitoring();
    return;
  }

  if (!config_url || !auth_token) {
    apply_session_timeout_config({
      inactivity_timeout_ms: fallback_inactivity_timeout_ms
    });
    return;
  }

  try {
    const response = await fetch(config_url, {
      method: "GET",
      headers: {
        Authorization: `Token ${auth_token}`,
        Accept: "application/json"
      }
    });

    if (response.status === 401 || response.status === 403) {
      await handle_idle_timeout();
      return;
    }

    if (!response.ok) {
      throw new Error(`Session timeout config returned ${response.status}.`);
    }

    const config = await response.json();

    if (request_id === config_request_id) {
      apply_session_timeout_config(config);
    }
  } catch (error) {
    console.warn("Using the default session timeout after config fetch failed.", error);
    apply_session_timeout_config({
      inactivity_timeout_ms: fallback_inactivity_timeout_ms
    });
  }
}

function start_config_refresh_timer() {
  clear_config_refresh_timer();
  config_refresh_timer_id = setInterval(refresh_session_timeout_config, config_refresh_ms);
}

function sync_session_timeout_monitor() {
  if (!should_monitor_current_route()) {
    stop_session_timeout_monitoring();
    return;
  }

  last_activity_at = Date.now();
  refresh_session_timeout_config();
  start_config_refresh_timer();
}

export function start_session_timeout_monitor(router) {
  if (!router || router_instance) {
    return;
  }

  router_instance = router;
  router.afterEach(sync_session_timeout_monitor);
  window.addEventListener("session-policy-updated", refresh_session_timeout_config);
  sync_session_timeout_monitor();
}
